import { useCallback, useEffect, useRef, useState } from 'react';

import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'constants/page';
import { handleError } from 'utils/errorHandler';

interface LazyMeta {
  page: number;
  size: number;
  total: number;
}

type LazyParam = Record<string, string | number | boolean | null | undefined>;

type GetDataFunction<TData> = (filters: LazyParam) => Promise<{
  data?: TData[];
  meta?: Partial<LazyMeta>;
}>;
type LazyParamInput = LazyParam | [LazyParam] | undefined;

interface UseLazyLoadingResult<TData> {
  loading: boolean;
  error: boolean;
  dataList: TData[];
  lastRef: (node: Element | null) => void;
  update: () => Promise<void>;
  reset: () => void;
}

const useLazyLoading = <TData>(
  getDataFunction: GetDataFunction<TData>,
  param?: LazyParamInput
): UseLazyLoadingResult<TData> => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const [dataList, setDataList] = useState<TData[]>([]);
  const [metaData, setMetaData] = useState<LazyMeta>({
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const normalizedParam = Array.isArray(param) ? param[0] : param;

  const update = useCallback(async (): Promise<void> => {
    const requestedPage = metaData.page;
    const filters = {
      ...normalizedParam,
      page: requestedPage,
      size: DEFAULT_PAGE_SIZE,
    };

    try {
      setLoading(true);
      setError(false);
      const { data, meta } = await getDataFunction(filters);
      const safeData = data ?? [];
      const safeMeta = {
        total: meta?.total ?? 0,
        size: meta?.size ?? DEFAULT_PAGE_SIZE,
        page: meta?.page ?? requestedPage,
      };

      setMetaData({ ...safeMeta, page: safeMeta.page + 1 });
      setDataList((currentDataList) => [...currentDataList, ...safeData]);
    } catch (error) {
      setError(true);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [getDataFunction, metaData.page, normalizedParam]);

  const reset = useCallback((): void => {
    setLoading(true);
    setError(false);
    setDataList([]);
    setMetaData({ page: DEFAULT_PAGE, size: DEFAULT_PAGE_SIZE, total: 0 });
  }, []);

  useEffect(() => {
    if (metaData.page !== DEFAULT_PAGE) {
      return;
    }

    const fetchInitialPage = async (): Promise<void> => {
      const filters = {
        ...normalizedParam,
        page: DEFAULT_PAGE,
        size: DEFAULT_PAGE_SIZE,
      };

      try {
        setLoading(true);
        setError(false);
        const { data, meta } = await getDataFunction(filters);
        const safeData = data ?? [];
        const safeMeta = {
          total: meta?.total ?? 0,
          size: meta?.size ?? DEFAULT_PAGE_SIZE,
          page: meta?.page ?? DEFAULT_PAGE,
        };

        setMetaData({ ...safeMeta, page: safeMeta.page + 1 });
        setDataList(safeData);
      } catch (error) {
        setError(true);
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    void fetchInitialPage();
  }, [getDataFunction, metaData.page, normalizedParam]);

  const lastRef = useCallback(
    (node: Element | null): void => {
      if (loading) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && dataList.length < metaData.total) {
          update();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [dataList.length, loading, metaData.total, update]
  );

  useEffect(() => {
    return () => {
      observer.current?.disconnect();
    };
  }, []);

  return { loading, error, dataList, lastRef, update, reset };
};

export default useLazyLoading;
